import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { ROUTES } from "../../../shared/utils";
import { AuthCardTopBar, AuthCardIntro } from "./AuthCardHeader";
import { useLogin } from "../mutations";
import { NewPasswordSetup } from "./NewPasswordSetup";
import { TemporaryPasswordSetup } from "./TemporaryPasswordSetup";
import { TermsOfServiceDialog } from "./TermsOfServiceDialog";

const SSO_DOMAINS = ["skewb.uk", "networkplus.co.uk"];

const isSsoDomain = (email: string) => {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? SSO_DOMAINS.includes(domain) : false;
};
interface LoginFormProps {
  onSuccess: (data?: { consent?: unknown; is_admin?: boolean }) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(() => {
    const ssoError = searchParams.get("sso_error");
    if (ssoError === "auth_failed") return "Single sign-on failed. Please try again or use your password.";
    if (ssoError === "no_code") return "Single sign-on was cancelled or incomplete.";
    if (ssoError === "invalid_token") return "Single sign-on returned an invalid response.";
    return "";
  });
  const [showNewPasswordSetup, setShowNewPasswordSetup] = useState(false);
  const [loginSession, setLoginSession] = useState("");
  const [showTempPasswordSetup, setShowTempPasswordSetup] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  const [forcePasswordLogin, setForcePasswordLogin] = useState(false);
  const showSso = isSsoDomain(email) && !forcePasswordLogin;

  const loginMutation = useLogin();

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 6;
  const showEmailError = touched && !isEmailValid;
  const showPasswordError = touched && !isPasswordValid;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    setError("");

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    try {
      const result = await loginMutation.mutateAsync({
        username: email.trim(),
        password: password.trim(),
      });

      if (result.data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setLoginSession(result.data.Session || "");
        setShowNewPasswordSetup(true);
      } else {
        onSuccess({ 
          consent: result.data.consent,
          is_admin: result.data.is_admin 
        });
      }
    } catch (error: unknown) {
      let errorMessage = "Login failed. Please try again.";

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              error?: string;
              requiresPasswordChange?: boolean;
            };
          };
        };

        if (axiosError.response?.status === 401) {
          errorMessage =
            "Invalid credentials. Please check your email and password.";
        } else if (
          axiosError.response?.data?.error === "PASSWORD_CHANGE_REQUIRED" ||
          axiosError.response?.data?.requiresPasswordChange
        ) {
          setTemporaryPassword(password.trim());
          setShowTempPasswordSetup(true);
          setError("");
          return;
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    }
  };

  const handleNewPasswordSuccess = () => {
    onSuccess();
  };

  const handleTemporaryPasswordSuccess = async (newPassword: string) => {
    setShowTempPasswordSetup(false);
    try {
      const result = await loginMutation.mutateAsync({
        username: email.trim(),
        password: newPassword,
      });
      onSuccess({
        consent: result.data.consent,
        is_admin: result.data.is_admin,
      });
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Login failed. Please try again."
      );
    }
  };

  const handleNewPasswordError = (error: string) => {
    setError(error);
    setShowNewPasswordSetup(false);
  };

  const handleTemporaryPasswordError = (error: string) => {
    setError(error);
    setShowTempPasswordSetup(false);
  };

  const handleSsoLogin = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || "https://localhost:8080";
    window.location.href = `${apiBase}/auth/sso/initiate?email=${encodeURIComponent(email.trim())}`;
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (showNewPasswordSetup) {
    return (
      <NewPasswordSetup
        username={email}
        session={loginSession}
        onSuccess={handleNewPasswordSuccess}
        onError={handleNewPasswordError}
      />
    );
  }

  if (showTempPasswordSetup) {
    return (
      <TemporaryPasswordSetup
        username={email.trim()}
        temporaryPassword={temporaryPassword}
        onSuccess={handleTemporaryPasswordSuccess}
        onError={handleTemporaryPasswordError}
      />
    );
  }

  return (
    <Card
      component="form"
      onSubmit={handleSubmit}
      variant="outlined"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "divider",
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 4,
        }}
      >
        <Stack spacing={4}>
          <AuthCardTopBar />
          <AuthCardIntro
            title="Ready for a healthier you?"
            subtitle="Log in with your email to track, manage, and feel better every day."
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email address"
              value={email}
              type="email"
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showEmailError}
              placeholder="Enter your email address"
              helperText={
                showEmailError ? "Please enter a valid email address." : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {!showSso && (
              <TextField
                fullWidth
                label="Password"
                value={password}
                type={showPassword ? "text" : "password"}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={() => setTouched(true)}
                error={showPasswordError}
                placeholder="Enter your password"
                helperText={
                  showPasswordError
                    ? "Password must be at least 6 characters long."
                    : ""
                }
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlineRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Stack>

          {showSso ? (
            <>
              <Button
                size="large"
                variant="contained"
                fullWidth
                onClick={handleSsoLogin}
                disabled={!isEmailValid}
                sx={{ mt: 4, mb: 1 }}
              >
                Sign in with Microsoft
              </Button>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  or use password
                </Typography>
              </Divider>
              <Button
                size="small"
                variant="text"
                fullWidth
                onClick={() => setForcePasswordLogin(true)}
                sx={{ mb: 2, textTransform: "none", color: "text.secondary" }}
              >
                Sign in with password instead
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Link
                  component={RouterLink}
                  to={ROUTES.RESET}
                  variant="body2"
                  underline="hover"
                  sx={{ fontWeight: 500 }}
                >
                  Forgot password?
                </Link>
              </Box>
              <Button
                type="submit"
                size="large"
                variant="contained"
                fullWidth
                endIcon={<ArrowForwardRoundedIcon />}
                disabled={
                  !email.length || !password.length || loginMutation.isPending
                }
                sx={{ mt: 4, mb: 2 }}
              >
                {loginMutation.isPending ? "Signing in..." : "Sign In"}
              </Button>
            </>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", fontWeight: 400 }}
          >
            Don&apos;t have an account?{" "}
            <Link
              component={RouterLink}
              to={ROUTES.SIGN_UP}
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Create one
            </Link>
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", fontWeight: 400 }}
          >
            Not ready to sign up?{" "}
            <Link
              component={RouterLink}
              to={ROUTES.REGISTER_INTEREST}
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Register your interest
            </Link>
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          px: { xs: 3, md: 4 },
          pt: 2,
          pb: { xs: 2.5, md: 3 },
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center" }}
        >
          By signing in, you agree to our{" "}
          <Link
            href="#"
            underline="hover"
            onClick={(event) => {
              event.preventDefault();
              setShowTermsDialog(true);
            }}
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="#"
            underline="hover"
            onClick={(event) => {
              event.preventDefault();
              setShowTermsDialog(true);
            }}
          >
            Privacy Notice
          </Link>
          .
        </Typography>
      </Box>
      <TermsOfServiceDialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />
    </Card>
  );
};
